import { Geometry } from '../places';
import { LanguageWithOther } from './language';
import { Specialty } from '../encounter';

import * as Practice from '../practice';
import * as Profile from '../providerProfile';
import * as ProviderOrganizationPreference from '../providerOrganizationPreference';
import * as User from '../user';

export type SupportedSexMap = {
  [key in User.Sex]?: number;
};

export type SupportedLanguageMap = {
  [key in LanguageWithOther]?: number;
};

export type SupportedPractice = Pick<Practice.Practice, 'id' | 'name'> & {
  count: number;
};

export type SupportedProviderTypeMap = {
  [key in ProviderType]?: number;
};

export type SupportedLocationMap = Record<string, number>;

export enum ProviderType {
  Physician = 'PHYSICIAN',
  PhysicianAssistant = 'PHYSICIAN_ASSISTANT',
  NursePractitioner = 'NURSE_PRACTITIONER',
}

export type ProviderTypeMap = {
  [key in ProviderType]: (User.DrSuffixMap | User.OtherSuffixMap)[];
};

export const providerTypeMapping: ProviderTypeMap = {
  PHYSICIAN: [
    User.DrSuffixMap.MD,
    User.DrSuffixMap.DO,
    User.DrSuffixMap.PsyD,
    User.DrSuffixMap.PHD,
  ],
  PHYSICIAN_ASSISTANT: [User.OtherSuffixMap.PA, User.OtherSuffixMap.PAC],
  NURSE_PRACTITIONER: [User.OtherSuffixMap.NP, User.OtherSuffixMap.DNP],
};

export const providerTypeMap: {
  [providerType in ProviderType]: string;
} = {
  PHYSICIAN: 'Physician',
  PHYSICIAN_ASSISTANT: 'Physician Assistant',
  NURSE_PRACTITIONER: 'Nurse Practitioner',
};

export interface RatingWithCount {
  value: number;
  count: number;
}

export type ProviderRating = {
  reputation: RatingWithCount;
};

export type SafeProviderWithPractices = User.SafeProvider & {
  practices: Practice.SafePracticeWithDistance[];
  profile?: Profile.Profile | null;
  preference?: ProviderOrganizationPreference.Preference | null;
  rating?: ProviderRating | null;
  id: string;
};

export type ProvidersWithSupportedValues = {
  providers: SafeProviderWithPractices[];
  supportedLanguages: SupportedLanguageMap;
  supportedLocations: SupportedLocationMap;
  supportedPractices: SupportedPractice[];
  supportedProviderTypes: SupportedProviderTypeMap;
  supportedSexes: SupportedSexMap;
  supportedSpecialties: Specialty[];
  acceptedPayorWithPlanTypes: string[];
};

interface FilterObject {
  specialty: string;
  geometry: Geometry;
  spokenLanguages: LanguageWithOther[];
  sexes: User.Sex[];
  offersVirtualVisits: boolean;
  offersOnlineScheduling: boolean;
  acceptsNewPatients: boolean;
  withinRadiusMiles: number;
  practiceIds: string[];
  providerTypes: ProviderType[];
  payorWithPlanTypes: string[];
  isPrimaryCareProvider: boolean;
  agesServed: ProviderOrganizationPreference.AgesServed[];
  locations: string[];
  ableToSchedule: boolean;
}

export type FilterOptions = Partial<FilterObject>;
